import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Card } from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';

const QuestionEditModal = ({ show, onHide, section, onSave }) => {
  const [questions, setQuestions] = useState([]);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (section) {
      setQuestions(section.questions || []);
    }
  }, [section]);

  const handleQuestionEdit = (question) => {
    setEditingQuestion(question);
  };

  const handleQuestionSave = async (questionData) => {
    const token = localStorage.getItem('adminToken');
    try {
      const url = questionData._id
        ? `${process.env.REACT_APP_API_BASE_URL}/api/questions/admin/section/${section._id}/question/${questionData._id}`
        : `${process.env.REACT_APP_API_BASE_URL}/api/questions/admin/section/${section._id}/question`;
      
      const method = questionData._id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(questionData)
      });

      if (!response.ok) throw new Error('Failed to save question');

      const result = await response.json();
      setQuestions(result.data.questions);
      setEditingQuestion(null);
      setError('');
      onSave();
    } catch (error) {
      setError(error.message);
    }
  };

  const handleQuestionDelete = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;

    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/questions/admin/section/${section._id}/question/${questionId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (!response.ok) throw new Error('Failed to delete question');

      const result = await response.json();
      setQuestions(result.data.questions);
      setError('');
      onSave();
    } catch (error) {
      setError(error.message);
    }
  };

  const handleAddQuestion = () => {
    setEditingQuestion({
      text: '',
      order: questions.length,
      isRequired: false,
      helpText: ''
    });
  };

  if (!section) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Edit Questions - {section.sectionTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        
        <div className="mb-3">
          <Button variant="success" onClick={handleAddQuestion}>
            <FaPlus /> Add Question
          </Button>
        </div>

        {questions.map((question, index) => (
          <Card key={question._id || index} className="mb-2">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <strong>Q{index + 1}:</strong> {question.text}
                  {question.helpText && (
                    <div className="text-muted small mt-1">
                      Help: {question.helpText}
                    </div>
                  )}
                  {question.isRequired && (
                    <span className="badge bg-warning ms-2">Required</span>
                  )}
                </div>
                <div>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleQuestionEdit(question)}
                    className="me-1"
                  >
                    <FaEdit />
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleQuestionDelete(question._id)}
                  >
                    <FaTrash />
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        ))}

        {editingQuestion && (
          <QuestionForm
            question={editingQuestion}
            onSave={handleQuestionSave}
            onCancel={() => setEditingQuestion(null)}
          />
        )}
      </Modal.Body>
    </Modal>
  );
};

const QuestionForm = ({ question, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    text: question.text || '',
    helpText: question.helpText || '',
    isRequired: question.isRequired || false,
    order: question.order || 0
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...question,
      ...formData
    });
  };

  return (
    <Card className="mt-3">
      <Card.Header>
        <h6 className="mb-0">
          {question._id ? 'Edit Question' : 'Add New Question'}
        </h6>
      </Card.Header>
      <Card.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Question Text</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formData.text}
              onChange={(e) => setFormData(prev => ({ ...prev, text: e.target.value }))}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Help Text (Optional)</Form.Label>
            <Form.Control
              type="text"
              value={formData.helpText}
              onChange={(e) => setFormData(prev => ({ ...prev, helpText: e.target.value }))}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Order</Form.Label>
            <Form.Control
              type="number"
              value={formData.order}
              onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) }))}
              min="0"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Required Question"
              checked={formData.isRequired}
              onChange={(e) => setFormData(prev => ({ ...prev, isRequired: e.target.checked }))}
            />
          </Form.Group>

          <div className="d-flex gap-2">
            <Button type="submit" variant="primary">
              {question._id ? 'Update Question' : 'Add Question'}
            </Button>
            <Button variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default QuestionEditModal;